import React, { useMemo } from 'react'
import { Button, CircularProgress, Box, InputAdornment, Avatar, Divider } from '@mui/material'
import { KEYS } from 'helpers/config'
import utils from 'helpers/utils'
import useReCaptcha from 'hooks/useReCaptcha'
import axios from 'providers/axios'
import TextFieldNN from 'components/TextFieldNN'
import PasswordInput from 'components/PasswordInput'
import { Link, useLocation } from 'react-router-dom'
import useShowError from 'hooks/useShowError'
import ClearIcon from '@mui/icons-material/Clear'
import passkey from 'helpers/integrations/passkey'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { AuthDeviceInfo } from 'hooks/useRedirectIfLoggedIn'

type Props = {
  authDevices: AuthDeviceInfo[]
  useAnotherAccount: () => void
  setLoading: (loading: boolean) => void
  setPasskeyDialog: (deviceId: string, redirectUrl: string) => void
}

const LoginPageEmail: React.FC<Props> = (props) => {
  const { authDevices, useAnotherAccount } = props

  const location = useLocation()
  const showError = useShowError()
  // const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const { reCaptchaLoaded, generateReCaptchaToken } = useReCaptcha()

  const requester = searchParams.get('requester')
  const isChromeExtension = requester === 'chrome-extension'

  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [mode, setMode] = React.useState<'EMAIL' | 'PASSWORD'>('EMAIL')
  const [error, setError] = React.useState('')
  const [passwordLoading, setPasswordLoading] = React.useState(false)

  const onEmailConfirm = async (email: string, hasPasskeyAuthenticator: boolean) => {
    const isValid = utils.isValidEmail(email)
    if (!isValid) return setError('Invalid email')
    setEmail(email)
    if (hasPasskeyAuthenticator) {
      try {
        props.setLoading(true)
        await passkey.login(email)
      } catch (error) {
        setMode('PASSWORD')
        props.setLoading(false)
      }
    } else {
      setMode('PASSWORD')
    }
  }

  const loginWithPassword = async () => {
    try {
      if (!password) throw new Error('Please enter your password')
      setPasswordLoading(true)
      const authDeviceInit = utils.getFullAuthDeviceData()
      const token = await generateReCaptchaToken('login')
      const { data } = await axios.post(
        '/login',
        { email, password, authDevice: authDeviceInit, redirectUrl: searchParams.get('redirectUrl') },
        {
          headers: {
            [KEYS.RECAPTCHA_TOKEN]: token,
            [KEYS.RECAPTCHA_ACTION]: 'login',
          },
        }
      )
      if (!data) return

      const authDevice = data.authDevice

      if (isChromeExtension) {
        utils.setChromeExtension(authDevice.deviceToken)
        return
      }

      const skipPasskey = localStorage.getItem(`passkey-skip-${authDevice.id}`)
      if (!data.hasPasskeyAuthenticator && !skipPasskey && browserSupportsWebAuthn()) {
        props.setPasskeyDialog(authDevice.id, data.redirectUrl)
      } else {
        window.location.href = data.redirectUrl
      }
    } catch (e) {
      showError(e)
      setPasswordLoading(false)
    }
  }

  const forgotPasswordLink = useMemo(() => {
    let link = '/request-password-reset'
    if (email) {
      const searchParams = new URLSearchParams({ email })
      link += `?${searchParams.toString()}`
    }
    return link
  }, [email])

  return (
    <Box>
      {mode === 'EMAIL' && (
        <>
          {authDevices.length === 0 && (
            <Box>
              <TextFieldNN
                label="Email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError('')
                }}
                type="email"
                id="email"
                size="small"
                autoComplete="username"
                onFocus={() => setError('')}
                onKeyDown={(e) => e.key === 'Enter' && onEmailConfirm((e.target as HTMLInputElement).value, false)}
                error={!!error}
                helperText={error}
              />

              <Button
                id="next-button"
                fullWidth
                color="primary"
                variant="contained"
                disabled={passwordLoading || !reCaptchaLoaded || !email}
                onClick={() => onEmailConfirm(email, false)}
              >
                {passwordLoading ? <CircularProgress size={20} /> : 'Next'}
              </Button>
            </Box>
          )}

          {authDevices.length > 0 && (
            <Box border="1px solid var(--light-grey-3)" borderRadius="12px">
              {authDevices.map(({ id, user, hasPasskeyAuthenticator }) => (
                <React.Fragment key={id}>
                  <Box
                    display="flex"
                    gap="12px"
                    alignItems="center"
                    p="12px 16px"
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'var(--light-grey-3)' },
                    }}
                    onClick={() => onEmailConfirm(user.email, hasPasskeyAuthenticator)}
                  >
                    <Avatar src={utils.getUrlFileMedia(user.nameFile)} alt={user.name} sx={{ width: 32, height: 32 }} />
                    <Box textAlign="left" overflow="hidden">
                      <Box className="fw-700" mb="8px">
                        {user.name}
                      </Box>
                      <Box
                        className="text-dark-grey-1 fs-14"
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {user.email}
                      </Box>
                    </Box>
                  </Box>

                  <Divider className="bg-light-grey-3" />
                </React.Fragment>
              ))}

              <Box
                display="flex"
                gap="12px"
                alignItems="center"
                p="12px 16px"
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'var(--light-grey-3)' },
                }}
                onClick={useAnotherAccount}
              >
                <Box width="32px">
                  <PersonAddIcon className="text-light-grey-2" />
                </Box>
                <Box textAlign="left" className="fs-14 text-dark-grey-1">
                  Use another account
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {mode === 'PASSWORD' && (
        <>
          <TextFieldNN
            id="email"
            disabled
            value={email}
            type="email"
            size="small"
            autoComplete="username"
            label="Email"
            endAdornment={
              <InputAdornment position="end">
                <ClearIcon
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    setEmail('')
                    setMode('EMAIL')
                  }}
                />
              </InputAdornment>
            }
          />

          <PasswordInput
            label="Password"
            onChange={(state) => setPassword(state.value)}
            validate={false}
            onKeyDownEnter={loginWithPassword}
            focusOnLoad
            autoComplete="current-password"
          />

          <Box textAlign="left" mb="12px">
            <Link to={forgotPasswordLink}>
              <Button size="small" id="btn-forgot-password">
                Forgot Password?
              </Button>
            </Link>
          </Box>

          <Box>
            <Button
              id="login-button"
              fullWidth
              color="primary"
              variant="contained"
              disabled={passwordLoading || !reCaptchaLoaded}
              onClick={loginWithPassword}
            >
              {passwordLoading ? <CircularProgress size={20} /> : 'Log In'}
            </Button>
            <Box className="text-red" mt="8px">
              {error}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default LoginPageEmail
